import { H4 } from "components/ui/typography/titles/Titles"
import { Checkbox } from "components/ui/checkbox/Checkbox"
import { useGetSeries } from "hooks/api/series/series.hook"
import { useEffect, useMemo, useState } from "react"
import { PricingTable } from "components/ui/tables/PricingTable/PricingTable"
import {
	PRICE_FIELDS,
	resolveDraftField,
	hasRowAllPrices,
	hasRowAnyPrice,
	hasRowNoPrices,
	type TDraftRow,
} from "components/ui/tables/PricingTable/PricingTable.helper"
import type {
	TModelNumberPricePayloadItem,
	TPriceField,
	TPricingInfo,
	TPricingSeries,
	TPricingTableRow,
} from "types/modelNumbers.type"
import style from "./Pricing.module.scss"

const { pricing, topBlock, filterBlock } = style

type Props = {
	pricingInfo?: TPricingInfo[]
	onDirtyChange?: (isDirty: boolean) => void
	onSavableChange?: (isSavable: boolean) => void
	onPayloadChange?: (payload: TModelNumberPricePayloadItem[]) => void
	showValidation?: boolean
}

export const Pricing = ({ pricingInfo, onDirtyChange, onSavableChange, onPayloadChange, showValidation }: Props) => {
	const { data: series } = useGetSeries()
	const [hideSeriesWithoutPrices, setHideSeriesWithoutPrices] = useState(false)
	const [draftPrices, setDraftPrices] = useState<Record<number, TDraftRow>>({})

	useEffect(() => {
		setDraftPrices({})
	}, [pricingInfo])

	const pricingBySeriesId = useMemo(() => {
		return (pricingInfo ?? []).reduce<Record<number, TPricingInfo>>((acc, item) => {
			acc[item.series.id] = item
			return acc
		}, {})
	}, [pricingInfo])

	const baseRows = useMemo<TPricingTableRow[]>(() => {
		return (series ?? []).map((seriesItem: TPricingSeries) => {
			const pricingItem = pricingBySeriesId[seriesItem.id]

			return {
				key: seriesItem.id,
				series_id: seriesItem.id,
				price_id: pricingItem?.id,
				series: seriesItem.series_name,
				min_price: pricingItem?.min_price,
				avg_price: pricingItem?.avg_price,
				max_price: pricingItem?.max_price,
				hasPrices: !!pricingItem,
			}
		})
	}, [series, pricingBySeriesId])

	const baseRowsBySeriesId = useMemo(() => {
		return baseRows.reduce<Record<number, TPricingTableRow>>((acc, row) => {
			acc[row.series_id] = row
			return acc
		}, {})
	}, [baseRows])

	const mergedRows = useMemo<TPricingTableRow[]>(() => {
		return baseRows.map((row) => {
			const draftRow = draftPrices[row.series_id]

			const minPrice = resolveDraftField(row.min_price, draftRow, "min_price")
			const avgPrice = resolveDraftField(row.avg_price, draftRow, "avg_price")
			const maxPrice = resolveDraftField(row.max_price, draftRow, "max_price")

			return {
				...row,
				min_price: minPrice,
				avg_price: avgPrice,
				max_price: maxPrice,
				hasPrices: hasRowAnyPrice({ min_price: minPrice, avg_price: avgPrice, max_price: maxPrice }),
			}
		})
	}, [baseRows, draftPrices])

	const pricingChanges = useMemo(() => {
		const payload: TModelNumberPricePayloadItem[] = []
		const incompleteDirtySeriesIds = new Set<number>()
		let isPricingDirty = false

		mergedRows.forEach((row) => {
			const baseRow = baseRowsBySeriesId[row.series_id]

			if (!baseRow) return

			const isRowDirty = PRICE_FIELDS.some((field) => baseRow[field] !== row[field])

			if (!isRowDirty) return

			isPricingDirty = true

			const rowPriceData = {
				min_price: row.min_price,
				avg_price: row.avg_price,
				max_price: row.max_price,
			}

			if (hasRowNoPrices(rowPriceData)) {
				if (baseRow.price_id) {
					payload.push({
						series_id: row.series_id,
						price_id: baseRow.price_id,
					})
				}

				return
			}

			if (!hasRowAllPrices(rowPriceData)) {
				incompleteDirtySeriesIds.add(row.series_id)
				return
			}

			payload.push({
				series_id: row.series_id,
				...(baseRow.price_id ? { price_id: baseRow.price_id } : {}),
				min_price: row.min_price,
				avg_price: row.avg_price,
				max_price: row.max_price,
			})
		})

		return {
			isPricingDirty,
			hasSavablePricingChanges: payload.length > 0 && incompleteDirtySeriesIds.size === 0,
			incompleteDirtySeriesIds,
			payload,
		}
	}, [mergedRows, baseRowsBySeriesId])

	useEffect(() => {
		onDirtyChange?.(pricingChanges.isPricingDirty)
		onSavableChange?.(pricingChanges.hasSavablePricingChanges)
		onPayloadChange?.(pricingChanges.payload)
	}, [pricingChanges, onDirtyChange, onSavableChange, onPayloadChange])

	const onCellValueChangeHandle = (seriesId: number, field: TPriceField, value: number | undefined) => {
		setDraftPrices((prev) => {
			const baseRow = baseRowsBySeriesId[seriesId]

			if (!baseRow) return prev

			const baseValue = baseRow[field]
			const nextFieldValue = value === undefined ? null : value
			const isSameAsInitial =
				(value === undefined && baseValue === undefined) || (value !== undefined && baseValue === value)

			const currentRowDraft = prev[seriesId] ?? {}
			const nextRowDraft = { ...currentRowDraft }

			if (isSameAsInitial) {
				delete nextRowDraft[field]
			} else {
				nextRowDraft[field] = nextFieldValue
			}

			if (!Object.keys(nextRowDraft).length) {
				const nextDraftPrices = { ...prev }
				delete nextDraftPrices[seriesId]
				return nextDraftPrices
			}

			return {
				...prev,
				[seriesId]: nextRowDraft,
			}
		})
	}

	const pricingTableData = useMemo<TPricingTableRow[]>(() => {
		const rows = mergedRows

		return hideSeriesWithoutPrices ? rows.filter((item) => item.hasPrices) : rows
	}, [mergedRows, hideSeriesWithoutPrices])

	return (
		<div className={pricing}>
			<div className={topBlock}>
				<H4>Pricing</H4>
				<div className={filterBlock}>
					<span>Hide series without prices</span>
					<Checkbox checked={hideSeriesWithoutPrices} onChange={(e) => setHideSeriesWithoutPrices(e.target.checked)} />
				</div>
			</div>
			<PricingTable data={pricingTableData} onCellValueChange={onCellValueChangeHandle} invalidSeriesIds={showValidation ? pricingChanges.incompleteDirtySeriesIds : undefined} />
		</div>
	)
}
